import { ShieldCheck } from '@orgus/ui/icons';
import type { AuditQueryResult, ChainVerifyResult } from '@orgus/protocol';
import { BarList, Button, Skeleton, formatInt, plural } from '@orgus/ui';
import type { BarListItem } from '@orgus/ui';
import { PageVerdict } from '../glance/PageVerdict.tsx';
import type { Verdict } from '../glance/PageVerdict.tsx';
import { auditActionLabel } from '../audit/labels.ts';

/** The audit log's one answer: is the record whole, and is anything missing. */
export function auditVerdict(r: AuditQueryResult | null, verify: ChainVerifyResult | null): Verdict {
  if (verify && !verify.ok) return { tone: 'danger', title: 'The audit log was changed', line: verify.summary };
  if (r?.archive.unreadable.length) return { tone: 'attention', title: 'Some archived entries cannot be read', line: 'Verify the chain to see where.' };
  if (verify?.ok) return { tone: 'ok', title: 'The record is whole', line: `${plural(verify.verified, 'entry', 'entries')} checked, nothing changed or removed.` };
  if (!r) return { tone: 'neutral', title: 'Reading the audit log' };
  return { tone: 'ok', title: r.total ? `${plural(r.total, 'action', 'actions')} on record` : 'Nothing recorded yet', line: 'Verify the chain to prove nothing changed.' };
}

const top = (facets: { value: string; count: number }[], label: (v: string) => string): BarListItem[] => {
  const sorted = [...facets].sort((a, b) => b.count - a.count);
  const head = sorted.slice(0, 4).map((f) => ({ key: f.value, label: label(f.value), value: f.count }));
  const rest = sorted.slice(4).reduce((n, f) => n + f.count, 0);
  return rest ? [...head, { key: '__other', label: 'Other', value: rest, other: true }] : head;
};

/** The answer, then who acted and what they did, as ranked bars. The table below is the detail. */
export function AuditGlance({ r, verify, verifying, onVerify }: { r: AuditQueryResult | null; verify: ChainVerifyResult | null; verifying: boolean; onVerify: () => void }) {
  const actors = r ? top(r.facets.actor, (v) => v) : [];
  const actions = r ? top(r.facets.action, auditActionLabel) : [];
  return (
    <div className="flex flex-col gap-4">
      <PageVerdict
        v={auditVerdict(r, verify)}
        action={
          <Button size="sm" variant="primary" icon={<ShieldCheck />} loading={verifying} onClick={onVerify}>
            Verify chain
          </Button>
        }
      />
      {!r ? (
        <div className="grid gap-4 rounded-card border border-border bg-surface p-4 sm:grid-cols-2" role="status" aria-label="Loading who did what">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : actors.length > 0 || actions.length > 0 ? (
        <section aria-label="Who did what" className="grid gap-6 rounded-card border border-border bg-surface px-4 py-4 shadow-elevation-flat sm:grid-cols-2 sm:px-8">
          <div className="flex min-w-0 flex-col gap-3">
            <p className="text-caption text-fg-subtle">Who acted</p>
            <BarList items={actors} format={formatInt} slot={1} label="Entries by actor" />
          </div>
          <div className="flex min-w-0 flex-col gap-3">
            <p className="text-caption text-fg-subtle">What they did</p>
            <BarList items={actions} format={formatInt} slot={3} label="Entries by action" />
          </div>
        </section>
      ) : null}
    </div>
  );
}
